/**
 * Freeform form validation module
 * Handles custom validation, error display, and blocked email detection
 */
export default function initFreeformValidation(formAnchor) {
  var form = document.querySelector('form[data-id="' + formAnchor + '"]');
  if (!form) {
    return;
  }

  form.addEventListener("freeform-ready", function (event) {
    var freeform = event.target.freeform;

    // Use a non-existent class for error banner so it doesn't break but doesn't display
    freeform.setOption("errorClassBanner", ["freeform-error-banner-hidden"]);
    freeform.setOption("errorClassList", ["help-block", "errors", "invalid-feedback"]);
    freeform.setOption("errorClassField", ["is-invalid", "has-error"]);
    freeform.setOption("successClassBanner", ["alert", "alert-success", "form-success"]);
  });
  
  form.addEventListener("freeform-stripe-styling", function (event) {
    event.detail.base = {
      fontSize: "16px",
      fontFamily: "-apple-system,BlinkMacSystemFont,\"Segoe UI\",Roboto,\"Helvetica Neue\",Arial,sans-serif,\"Apple Color Emoji\",\"Segoe UI Emoji\",\"Segoe UI Symbol\",\"Noto Color Emoji\"",
    };
  });

  // Listen for AJAX errors (blocked emails and other errors come through here)
  form.addEventListener("freeform-ajax-error", function(event) {
    var response = event.response || {};
    var errors = response.errors || {};
    var hasBlockedEmailError = false;
    var blockedEmailMessage = '';
    var hasGeneralError = false;
    var generalErrorMessage = '';
    
    // Debug: log the response to see what we're getting
    console.log('Freeform AJAX Error:', response);
    
    // Check for honeypot errors first (these can appear even when success: true)
    // Honeypot errors typically mean the form was flagged as spam
    if (response.honeypot && response.honeypot.errorMessage) {
      hasGeneralError = true;
      // Provide a user-friendly message instead of the technical honeypot message
      generalErrorMessage = 'There was an error submitting the form. Please try again later.';
      console.log('Honeypot error detected:', response.honeypot.errorMessage);
    }
    
    // Handle errors object structure: {fieldName: [errorMessages]}
    // Check if errors is an object (not an array)
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
      // Iterate through each field that has errors
      for (var fieldName in errors) {
        if (errors.hasOwnProperty(fieldName)) {
          var fieldErrors = errors[fieldName];
          
          // Check if this is an email field
          if (fieldName.toLowerCase().indexOf('email') !== -1) {
            // Get the first error message for this field
            if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
              var errorMsg = fieldErrors[0];
              hasBlockedEmailError = true;
              blockedEmailMessage = errorMsg || 'There was an error submitting the form. Please try again later.';
              console.log('Blocked email error detected on field:', fieldName, blockedEmailMessage);
              break; // Found email error, stop looking
            } else if (typeof fieldErrors === 'string') {
              hasBlockedEmailError = true;
              blockedEmailMessage = fieldErrors || 'There was an error submitting the form. Please try again later.';
              console.log('Blocked email error detected on field:', fieldName, blockedEmailMessage);
              break;
            }
          }
        }
      }
    }
    
    // Also handle array structure (fallback)
    if (!hasBlockedEmailError && Array.isArray(errors) && errors.length > 0) {
      errors.forEach(function(error) {
        var message = (error.message || '').toLowerCase();
        var fieldName = (error.fieldName || '').toLowerCase();
        
        // Check if this is a blocked email error
        if (message.indexOf('error submitting') !== -1 || 
            message.indexOf('blocked') !== -1 ||
            message.indexOf('invalid email') !== -1 ||
            message.indexOf('not allowed') !== -1 ||
            message.indexOf('disallowed') !== -1 ||
            (fieldName.indexOf('email') !== -1 && message && message.length > 0)) {
          hasBlockedEmailError = true;
          blockedEmailMessage = error.message || 'There was an error submitting the form. Please try again later.';
          console.log('Blocked email error detected:', blockedEmailMessage);
        } else if (message && message.length > 0) {
          // Capture any other error messages
          hasGeneralError = true;
          generalErrorMessage = error.message || 'An unknown error occurred.';
        }
      });
    }
    
    // Also check the response message directly
    if (!hasBlockedEmailError && response.message) {
      var responseMsg = (response.message || '').toLowerCase();
      if (responseMsg.indexOf('error submitting') !== -1 || 
          responseMsg.indexOf('blocked') !== -1 ||
          responseMsg.indexOf('invalid email') !== -1 ||
          responseMsg.indexOf('not allowed') !== -1 ||
          responseMsg.indexOf('disallowed') !== -1) {
        hasBlockedEmailError = true;
        blockedEmailMessage = response.message || 'There was an error submitting the form. Please try again later.';
        console.log('Blocked email error detected in response.message:', blockedEmailMessage);
      } else if (responseMsg && responseMsg.length > 0 && responseMsg !== 'ok') {
        // Capture any other error messages
        hasGeneralError = true;
        generalErrorMessage = response.message || 'An unknown error occurred.';
        console.log('General error detected in response.message (not email-related):', {
          message: response.message,
          responseMsg: responseMsg,
          fullResponse: response
        });
      }
    }
    
    // Also check if any email field has the is-invalid class (indicating an error)
    // This catches cases where Freeform marks the email field as invalid but doesn't provide a clear error message
    if (!hasBlockedEmailError) {
      var emailFields = form.querySelectorAll('input[type="email"].is-invalid, input[type="email"].has-error');
      if (emailFields.length > 0) {
        // Try to get the actual error message from the field's error container
        var errorMessage = '';
        emailFields.forEach(function(emailField) {
          var errorContainer = emailField.parentElement.querySelector('.invalid-feedback, .help-block');
          if (errorContainer && errorContainer.textContent && errorContainer.textContent.trim()) {
            errorMessage = errorContainer.textContent.trim();
          }
        });
        
        hasBlockedEmailError = true;
        blockedEmailMessage = errorMessage || 'There was an error submitting the form. Please try again later.';
        console.log('Blocked email error detected via invalid email field class:', blockedEmailMessage);
      }
    }
    
    // If we have a blocked email error, show that banner
    if (hasBlockedEmailError) {
      console.log('Showing error banner:', blockedEmailMessage);
      
      // Hide any inline field errors - specifically target email fields first
      // BUT: Don't remove our custom validation errors (js-error class) - those are for required fields
      var emailFields = form.querySelectorAll('input[type="email"]');
      emailFields.forEach(function(field) {
        // Only remove server-side errors, not our custom validation errors
        var customErrorContainer = field.parentElement.querySelector('.invalid-feedback.js-error');
        if (!customErrorContainer) {
          // No custom validation error, safe to remove server errors
          field.classList.remove('is-invalid', 'has-error');
          var errorContainers = field.parentElement.querySelectorAll('.invalid-feedback:not(.js-error), .help-block');
          errorContainers.forEach(function(container) {
            container.remove();
          });
        }
      });
      
      // Also remove from all other fields
      var allFields = form.querySelectorAll('input:not([type="email"]), select, textarea');
      allFields.forEach(function(field) {
        field.classList.remove('is-invalid', 'has-error');
        var errorContainers = field.parentElement.querySelectorAll('.invalid-feedback, .help-block');
        errorContainers.forEach(function(container) {
          container.remove();
        });
      });
      
      // Create or update error banner above the form
      showErrorBannerAboveForm(form, blockedEmailMessage);
      
      // Use setTimeout to ensure we remove the class even if Freeform adds it back
      // BUT: Don't remove our custom validation errors (js-error class) - those are for required fields
      setTimeout(function() {
        emailFields.forEach(function(field) {
          // Only remove server-side errors, not our custom validation errors
          var customErrorContainer = field.parentElement.querySelector('.invalid-feedback.js-error');
          if (!customErrorContainer) {
            // No custom validation error, safe to remove server errors
            field.classList.remove('is-invalid', 'has-error');
          }
        });
      }, 100);
      
      // Prevent default error handling
      event.preventDefault();
    } else if (hasGeneralError) {
      // Handle other errors - show a generic message
      console.log('Showing general error banner:', generalErrorMessage);
      showErrorBannerAboveForm(form, generalErrorMessage);
      // Prevent default error handling
      event.preventDefault();
    } else {
      // If we can't identify the error, show a generic message and prevent default
      console.log('Unknown error occurred, showing generic message');
      console.log('Full error response structure:', {
        response: response,
        errors: errors,
        responseType: typeof response,
        errorsType: typeof errors,
        responseKeys: response ? Object.keys(response) : [],
        errorsKeys: errors && typeof errors === 'object' ? Object.keys(errors) : [],
        emailFields: form.querySelectorAll('input[type="email"]').length,
        invalidEmailFields: form.querySelectorAll('input[type="email"].is-invalid, input[type="email"].has-error').length
      });
      showErrorBannerAboveForm(form, 'An unknown error occurred. Please try again later.');
      event.preventDefault();
    }
  });

  // Also listen for success events to catch honeypot errors that come through as success: true
  form.addEventListener("freeform-ajax-success", function(event) {
    var response = event.response || {};
    
    // Only treat honeypot as error if there's NO submissionToken (submission was blocked)
    // If there IS a token, the submission went through despite the honeypot warning
    if (response.honeypot && response.honeypot.errorMessage && !response.submissionToken) {
      console.log('Honeypot error detected in success handler - submission blocked:', response.honeypot.errorMessage);
      // Prevent default success handling
      event.preventDefault();
      // Show user-friendly error message instead of technical honeypot message
      showErrorBannerAboveForm(form, 'There was an error submitting the form. Please try again later.');
    } else if (response.honeypot && response.honeypot.errorMessage && response.submissionToken) {
      // Honeypot warning but submission has token - log but allow success
      console.log('Honeypot warning detected but submission has token - allowing success:', response.honeypot.errorMessage);
    }
  });

  // Handle Freeform errors - show blocked email errors above form, prevent others
  form.addEventListener("freeform-render-form-errors", function(event) {
    var errors = event.errors || [];
    var hasBlockedEmailError = false;
    var blockedEmailMessage = '';
    var hasGeneralError = false;
    var generalErrorMessage = '';
    
    console.log('freeform-render-form-errors event:', errors);
    
    // Check if any error is a blocked email error
    if (errors && errors.length > 0) {
      errors.forEach(function(error) {
        var message = (error.message || '').toLowerCase();
        var fieldName = (error.fieldName || '').toLowerCase();
        var fieldHandle = (error.handle || '').toLowerCase();
        var errorObj = error.error || '';
        
        console.log('Checking error in render:', { message: message, fieldName: fieldName, fieldHandle: fieldHandle, error: error });
        
        // Check if this is a blocked email error - be more aggressive
        if (message.indexOf('error submitting') !== -1 || 
            message.indexOf('blocked') !== -1 ||
            message.indexOf('invalid email') !== -1 ||
            message.indexOf('not allowed') !== -1 ||
            message.indexOf('disallowed') !== -1 ||
            (fieldName.indexOf('email') !== -1 && message && message.length > 0) ||
            (fieldHandle.indexOf('email') !== -1 && message && message.length > 0) ||
            (errorObj && errorObj.toLowerCase().indexOf('blocked') !== -1)) {
          hasBlockedEmailError = true;
          blockedEmailMessage = error.message || 'There was an error submitting the form. Please try again later.';
          console.log('Blocked email error detected in render:', blockedEmailMessage);
        } else if (message && message.length > 0) {
          // Capture any other error messages
          hasGeneralError = true;
          generalErrorMessage = error.message || 'An unknown error occurred.';
          console.log('General error detected in render-form-errors (not email-related):', {
            message: error.message,
            fieldName: error.fieldName,
            fieldHandle: error.handle,
            fullError: error
          });
        }
      });
    }
    
    // Also check if any email fields have errors
    if (!hasBlockedEmailError) {
      var emailFields = form.querySelectorAll('input[type="email"]');
      emailFields.forEach(function(emailField) {
        if (emailField.classList.contains('is-invalid') || emailField.classList.contains('has-error')) {
          // Check if there's an error message for this field
          var fieldErrors = errors.filter(function(err) {
            var errFieldName = (err.fieldName || '').toLowerCase();
            var errHandle = (err.handle || '').toLowerCase();
            var fieldName = (emailField.name || '').toLowerCase();
            return errFieldName === fieldName || errHandle === fieldName;
          });
          
          if (fieldErrors.length > 0) {
            hasBlockedEmailError = true;
            blockedEmailMessage = fieldErrors[0].message || 'There was an error submitting the form. Please try again later.';
            console.log('Blocked email error detected via email field with errors:', blockedEmailMessage);
          } else {
            // Email field is marked invalid but no matching error found - still treat as blocked email
            // Try to get the actual error message from the field's error container
            var errorMessage = '';
            var errorContainer = emailField.parentElement.querySelector('.invalid-feedback, .help-block');
            if (errorContainer && errorContainer.textContent && errorContainer.textContent.trim()) {
              errorMessage = errorContainer.textContent.trim();
            }
            
            hasBlockedEmailError = true;
            blockedEmailMessage = errorMessage || 'There was an error submitting the form. Please try again later.';
            console.log('Blocked email error detected via invalid email field class (no matching error):', blockedEmailMessage);
          }
        }
      });
    }
    
    // Always prevent default error rendering - we'll handle it ourselves
    event.preventDefault();
    
    if (hasBlockedEmailError) {
      console.log('Preventing default error rendering and showing banner');
      
      // Hide any inline field errors - specifically target email fields first
      // BUT: Don't remove our custom validation errors (js-error class) - those are for required fields
      var emailFields = form.querySelectorAll('input[type="email"]');
      emailFields.forEach(function(field) {
        // Only remove server-side errors, not our custom validation errors
        var customErrorContainer = field.parentElement.querySelector('.invalid-feedback.js-error');
        if (!customErrorContainer) {
          // No custom validation error, safe to remove server errors
          field.classList.remove('is-invalid', 'has-error');
          var errorContainers = field.parentElement.querySelectorAll('.invalid-feedback:not(.js-error), .help-block');
          errorContainers.forEach(function(container) {
            container.remove();
          });
        }
      });
      
      // Also remove from all other fields
      var allFields = form.querySelectorAll('input:not([type="email"]), select, textarea');
      allFields.forEach(function(field) {
        field.classList.remove('is-invalid', 'has-error');
        var errorContainers = field.parentElement.querySelectorAll('.invalid-feedback, .help-block');
        errorContainers.forEach(function(container) {
          container.remove();
        });
      });
      
      // Create or update error banner above the form
      showErrorBannerAboveForm(form, blockedEmailMessage);
      
      // Use setTimeout to ensure we remove the class even if Freeform adds it back
      // BUT: Don't remove our custom validation errors (js-error class) - those are for required fields
      setTimeout(function() {
        emailFields.forEach(function(field) {
          // Only remove server-side errors, not our custom validation errors
          var customErrorContainer = field.parentElement.querySelector('.invalid-feedback.js-error');
          if (!customErrorContainer) {
            // No custom validation error, safe to remove server errors
            field.classList.remove('is-invalid', 'has-error');
          }
        });
      }, 100);
    } else if (hasGeneralError) {
      // Handle other errors - show a generic message
      console.log('Showing general error banner:', generalErrorMessage);
      
      // Hide any inline field errors
      var allFields = form.querySelectorAll('input, select, textarea');
      allFields.forEach(function(field) {
        field.classList.remove('is-invalid', 'has-error');
        var errorContainers = field.parentElement.querySelectorAll('.invalid-feedback, .help-block');
        errorContainers.forEach(function(container) {
          container.remove();
        });
      });
      
      showErrorBannerAboveForm(form, generalErrorMessage);
    } else {
      // For validation errors (our custom ones), prevent default rendering but keep them visible
      // Only hide Freeform's error banners, not our validation errors
      var freeformBanners = form.querySelectorAll('.alert-danger:not(.freeform-error-banner-top), .errors:not(.invalid-feedback), .help-block:not(.invalid-feedback)');
      freeformBanners.forEach(function(banner) {
        banner.style.display = 'none';
      });
      
      // Ensure our validation errors remain visible
      var ourErrors = form.querySelectorAll('.invalid-feedback.js-error');
      ourErrors.forEach(function(error) {
        if (error.textContent && error.textContent.trim().length > 0) {
          error.style.display = 'block';
        }
      });
    }
  });
  
  // Also listen for when Freeform adds invalid classes to email fields
  // Use MutationObserver to detect when email fields get the is-invalid class
  // For blocked email errors, remove the red border and show banner instead
  // BUT: Don't remove our custom validation errors (js-error class) - those are for required fields
  var emailFields = form.querySelectorAll('input[type="email"]');
  emailFields.forEach(function(emailField) {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (emailField.classList.contains('is-invalid') || emailField.classList.contains('has-error')) {
            // Check if this is our custom validation error (has js-error class)
            var customErrorContainer = emailField.parentElement.querySelector('.invalid-feedback.js-error');
            if (customErrorContainer) {
              // This is a custom validation error (like "This field is required")
              // Don't remove it - let it display normally
              return;
            }
            
            // This is a server-side error (blocked email, etc.) - convert to banner
            // Check if there's already a banner - if not, show one
            var formParent = form.parentElement || form.closest('.block__inner') || form.closest('.modal__content') || form.closest('.block');
            if (formParent) {
              var existingBanner = formParent.querySelector('.freeform-error-banner-top');
              if (!existingBanner || existingBanner.style.display === 'none') {
                console.log('Email field marked invalid (server error), showing banner and removing red border');
                showErrorBannerAboveForm(form, 'There was an error submitting the form. Please try again later.');
                // Remove the red border from the email field
                emailField.classList.remove('is-invalid', 'has-error');
                // Also remove any error containers below the field (but not js-error ones)
                var errorContainers = emailField.parentElement.querySelectorAll('.invalid-feedback:not(.js-error), .help-block');
                errorContainers.forEach(function(container) {
                  container.remove();
                });
              } else {
                // Banner already exists, just remove the red border
                emailField.classList.remove('is-invalid', 'has-error');
                var errorContainers = emailField.parentElement.querySelectorAll('.invalid-feedback:not(.js-error), .help-block');
                errorContainers.forEach(function(container) {
                  container.remove();
                });
              }
            }
          }
        }
      });
    });
    
    observer.observe(emailField, {
      attributes: true,
      attributeFilter: ['class']
    });
  });
  
  function showErrorBannerAboveForm(form, message) {
    console.log('showErrorBannerAboveForm called with message:', message);
    
    // Find or create error banner container above the form
    var formParent = form.parentElement;
    if (!formParent) {
      // If form doesn't have a parent, try to find the form wrapper
      formParent = form.closest('.block__inner') || form.closest('.modal__content') || form.closest('.block') || document.body;
    }
    
    console.log('Form parent:', formParent);
    
    var errorBanner = formParent.querySelector('.freeform-error-banner-top');
    
    if (!errorBanner) {
      console.log('Creating new error banner');
      // Create error banner
      errorBanner = document.createElement('div');
      errorBanner.className = 'freeform-error-banner-top';
      errorBanner.setAttribute('role', 'alert');
      
      // Try to insert before the form, or at the beginning of the parent
      if (formParent.contains(form)) {
        formParent.insertBefore(errorBanner, form);
      } else {
        // If form is not a direct child, prepend to parent
        formParent.insertBefore(errorBanner, formParent.firstChild);
      }
    }
    
    // Set the error message
    errorBanner.textContent = message;
    errorBanner.style.display = 'block';
    
    console.log('Error banner created/updated:', errorBanner);
    console.log('Error banner display:', window.getComputedStyle(errorBanner).display);
    console.log('Error banner text:', errorBanner.textContent);
    
    // Scroll to the error banner
    setTimeout(function() {
      errorBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }

  // Disable HTML5 validation - we'll handle it with custom validation
  form.setAttribute('novalidate', 'novalidate');
  
  // Remove required attributes to prevent HTML5 validation
  var allFields = form.querySelectorAll('input, select, textarea');
  allFields.forEach(function(field) {
    // Store if field was required for our custom validation
    if (field.hasAttribute('required')) {
      field.setAttribute('data-required', 'true');
      field.removeAttribute('required');
    }
    // Remove pattern attribute from name fields
    if (field.hasAttribute('pattern')) {
      field.removeAttribute('pattern');
    }
    
    // Clear field error messages when user changes the value
    function clearFieldError() {
      field.classList.remove('is-invalid', 'has-error');
      // Remove all error containers (including Freeform's and ours)
      var errorContainers = field.parentElement.querySelectorAll('.invalid-feedback');
      errorContainers.forEach(function(container) {
        container.remove();
      });
      
      // Clear error banner when user starts typing in email fields
      if (field.type === 'email') {
        var formParent = form.parentElement || form.closest('.block__inner') || form.closest('.modal__content') || form.closest('.block');
        if (formParent) {
          var errorBanner = formParent.querySelector('.freeform-error-banner-top');
          if (errorBanner) {
            errorBanner.style.display = 'none';
            errorBanner.textContent = '';
          }
        }
      }
    }
    
    // For select fields, clear error on change
    if (field.tagName === 'SELECT') {
      field.addEventListener('change', clearFieldError);
    }
    
    // For text inputs and textareas, clear error on input
    if (field.type === 'text' || field.type === 'email' || field.type === 'tel' || field.tagName === 'TEXTAREA') {
      field.addEventListener('input', clearFieldError);
    }
    
    // For checkboxes and radios, clear error on change
    if (field.type === 'checkbox' || field.type === 'radio') {
      field.addEventListener('change', function() {
        // Clear error for all fields in the same group
        var groupFields = form.querySelectorAll('[name="' + field.name + '"]');
        groupFields.forEach(function(groupField) {
          groupField.classList.remove('is-invalid', 'has-error');
          var errorContainers = groupField.parentElement.querySelectorAll('.invalid-feedback');
          errorContainers.forEach(function(container) {
            container.remove();
          });
        });
      });
    }
  });

  // Validate all fields on form submission (pre-submit validation)
  form.addEventListener('submit', function(event) {
    // Clear all previous errors first
    clearAllErrors();
    
    var isFormValid = true;
    var allFormFields = form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]), select, textarea');
    
    // Also check checkboxes and radios
    var checkboxGroups = {};
    var radioGroups = {};
    var checkboxes = form.querySelectorAll('input[type="checkbox"][data-required]');
    var radios = form.querySelectorAll('input[type="radio"][data-required]');
    
    // Group checkboxes and radios by name
    checkboxes.forEach(function(cb) {
      if (!checkboxGroups[cb.name]) {
        checkboxGroups[cb.name] = [];
      }
      checkboxGroups[cb.name].push(cb);
    });
    
    radios.forEach(function(radio) {
      if (!radioGroups[radio.name]) {
        radioGroups[radio.name] = [];
      }
      radioGroups[radio.name].push(radio);
    });
    
    // Validate all text/select/textarea fields
    allFormFields.forEach(function(field) {
      var fieldErrors = [];
      
      // Special handling for select fields - ensure "Select" is treated as invalid
      if (field.tagName === 'SELECT') {
        var isRequired = field.hasAttribute('data-required') || field.hasAttribute('required') || field.required;
        if (isRequired) {
          var selectedOption = null;
          if (field.selectedIndex >= 0 && field.selectedIndex < field.options.length) {
            selectedOption = field.options[field.selectedIndex];
          }
          if (selectedOption) {
            var selectedText = selectedOption.textContent.trim().toLowerCase();
            var selectedValue = selectedOption.value || '';
            // If "Select" is selected or value is empty, treat as invalid
            if (selectedText === 'select' || selectedText === '--' || selectedText === 'choose' || selectedText === 'please select' || selectedValue === '' || selectedOption.disabled) {
              fieldErrors.push('Please select an option.');
            }
          }
        }
      }
      
      // If we didn't already find errors for select fields, run normal validation
      if (fieldErrors.length === 0) {
        fieldErrors = validateField(field);
      }
      
      if (fieldErrors.length > 0) {
        isFormValid = false;
        showFieldErrors(field, fieldErrors);
      }
    });
    
    // Validate checkbox groups
    for (var groupName in checkboxGroups) {
      var group = checkboxGroups[groupName];
      var hasChecked = group.some(function(cb) { return cb.checked; });
      if (!hasChecked) {
        isFormValid = false;
        var firstCheckbox = group[0];
        showFieldErrors(firstCheckbox, ['At least one option must be selected.']);
      }
    }
    
    // Validate radio groups
    for (var radioGroupName in radioGroups) {
      var radioGroup = radioGroups[radioGroupName];
      var hasRadioChecked = radioGroup.some(function(radio) { return radio.checked; });
      if (!hasRadioChecked) {
        isFormValid = false;
        var firstRadio = radioGroup[0];
        showFieldErrors(firstRadio, ['Please select an option.']);
      }
    }
    
    if (!isFormValid) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      // Ensure errors remain visible after a brief delay (in case Freeform tries to clear them)
      setTimeout(function() {
        // Re-show any errors that might have been hidden
        var allInvalidFields = form.querySelectorAll('.is-invalid, .has-error');
        allInvalidFields.forEach(function(field) {
          var errorContainer = field.parentElement.querySelector('.invalid-feedback.js-error');
          if (!errorContainer) {
            // If error container was removed, we need to find which field this is and re-show its error
            // This shouldn't happen, but just in case
            var fieldErrors = validateField(field);
            if (fieldErrors.length > 0) {
              showFieldErrors(field, fieldErrors);
            }
          } else if (errorContainer.style.display === 'none' || !errorContainer.textContent) {
            // Re-show the error if it was hidden or cleared
            var fieldErrors = validateField(field);
            if (fieldErrors.length > 0) {
              errorContainer.textContent = fieldErrors[0];
              errorContainer.style.display = 'block';
            }
          }
        });
        
        // Scroll to first error
        var firstError = form.querySelector('.is-invalid');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstError.focus();
        }
      }, 150);
      
      return false;
    }
    
    // If all valid, allow form to submit normally
  }, true); // Use capture phase to run before Freeform's handlers

  function clearAllErrors() {
    var allFields = form.querySelectorAll('input, select, textarea');
    allFields.forEach(function(field) {
      field.classList.remove('is-invalid', 'has-error');
      // Remove all error containers (including Freeform's server-rendered ones)
      var errorContainers = field.parentElement.querySelectorAll('.invalid-feedback');
      errorContainers.forEach(function(container) {
        container.remove();
      });
    });
    
    // Also clear any Freeform-generated error messages
    var freeformErrors = form.querySelectorAll('.help-block, .errors, .alert-danger');
    freeformErrors.forEach(function(error) {
      error.style.display = 'none';
    });
    
    // Clear error banner above form
    var formParent = form.parentElement || form.closest('.block__inner') || form.closest('.modal__content') || form.closest('.block');
    if (formParent) {
      var errorBanner = formParent.querySelector('.freeform-error-banner-top');
      if (errorBanner) {
        errorBanner.style.display = 'none';
        errorBanner.textContent = '';
      }
    }
  }
  
  // On page load, remove all Freeform server-rendered errors
  // They'll be shown via our JavaScript validation instead
  setTimeout(function() {
    var allServerErrors = form.querySelectorAll('.invalid-feedback:not(.js-error)');
    allServerErrors.forEach(function(error) {
      error.remove();
    });
  }, 100);

  function getErrorContainer(field) {
    // Remove ALL existing error containers first (including Freeform's server-rendered ones)
    var existingContainers = field.parentElement.querySelectorAll('.invalid-feedback');
    existingContainers.forEach(function(container) {
      container.remove();
    });
    
    // Create a new error container with js-error class so it displays
    var container = document.createElement('div');
    container.className = 'invalid-feedback js-error';
    
    // For checkboxes/radios, insert after the label
    if (field.type === 'checkbox' || field.type === 'radio') {
      var label = field.parentElement.querySelector('label');
      if (label && label.nextSibling) {
        field.parentElement.insertBefore(container, label.nextSibling);
      } else {
        field.parentElement.appendChild(container);
      }
    } else {
      // For select, input, textarea - insert after the field
      // Check if there are instructions or other elements after the field
      var parentContainer = field.parentElement;
      var nextSibling = field.nextSibling;
      
      // Skip text nodes and find the next element sibling
      while (nextSibling && nextSibling.nodeType !== 1) {
        nextSibling = nextSibling.nextSibling;
      }
      
      if (nextSibling) {
        parentContainer.insertBefore(container, nextSibling);
      } else {
        // If no next sibling, append to parent (form-group)
        parentContainer.appendChild(container);
      }
    }
    
    return container;
  }

  function showFieldErrors(field, errors) {
    field.classList.add('is-invalid', 'has-error');
    
    // Create a single error container (getErrorContainer already removes existing ones)
    var errorContainer = getErrorContainer(field);
    if (errorContainer && errors.length > 0) {
      // Display the first error message
      errorContainer.textContent = errors[0];
      // Ensure it has the js-error class and is visible
      errorContainer.classList.add('js-error');
      errorContainer.style.display = 'block';
    }
  }

  function validateField(field) {
    var errors = [];
    // Check for required attribute (both data-required and required)
    var isRequired = field.hasAttribute('data-required') || field.hasAttribute('required') || field.required;
    var fieldValue = '';
    var fieldType = field.type || field.tagName.toLowerCase();
    var fieldHandle = field.name || '';
    var fieldLabel = '';
    
    // Get field label for better error messages
    var labelElement = field.parentElement.querySelector('label');
    if (!labelElement) {
      // Try to find label by for attribute
      var fieldId = field.id;
      if (fieldId) {
        labelElement = form.querySelector('label[for="' + fieldId + '"]');
      }
    }
    if (labelElement) {
      fieldLabel = labelElement.textContent.trim().replace(/\s*\*?\s*$/, '');
    }
    
    // Get field value based on type
    if (field.tagName === 'SELECT') {
      // For select fields, check the actual selected option, not just field.value
      // This is important because disabled options may not set field.value correctly
      var selectedOption = null;
      if (field.selectedIndex >= 0 && field.selectedIndex < field.options.length) {
        selectedOption = field.options[field.selectedIndex];
      }
      
      // Get the selected option's text (normalized to lowercase for comparison)
      var selectedText = selectedOption ? selectedOption.textContent.trim().toLowerCase() : '';
      
      // Treat as empty if:
      // 1. Selected option is disabled
      // 2. Selected option has empty value
      // 3. Selected option text is "select" (common placeholder text)
      // 4. Field value is empty
      var isSelectPlaceholder = selectedText === 'select' || selectedText === '--' || selectedText === 'choose' || selectedText === 'please select';
      if (selectedOption && (selectedOption.disabled || selectedOption.value === '' || isSelectPlaceholder)) {
        fieldValue = '';
      } else {
        fieldValue = field.value || '';
      }
    } else if (fieldType === 'checkbox' || fieldType === 'radio') {
      // Skip checkbox/radio validation here - handled separately in submit handler
      return errors;
    } else {
      fieldValue = (field.value || '').trim();
    }
    
    // Check if field is empty (for required fields)
    if (isRequired) {
      var isEmpty = false;
      
      if (field.tagName === 'SELECT') {
        // Get the selected option for validation
        var selectedOption = null;
        if (field.selectedIndex >= 0 && field.selectedIndex < field.options.length) {
          selectedOption = field.options[field.selectedIndex];
        }
        
        // Get the selected option's text (normalized to lowercase)
        var selectedText = selectedOption ? selectedOption.textContent.trim().toLowerCase() : '';
        
        // Check multiple conditions for empty state:
        // 1. No field value
        // 2. Empty field value
        // 3. First option is placeholder (empty value) and it's selected
        // 4. Selected option is disabled
        // 5. Selected option has empty value
        // 6. Selected option text is "select" or other common placeholder text
        var hasPlaceholder = field.options.length > 0 && field.options[0].value === '';
        var isPlaceholderSelected = hasPlaceholder && field.selectedIndex === 0;
        var isDisabledOrEmptyOption = selectedOption && (selectedOption.disabled || selectedOption.value === '');
        var isSelectPlaceholder = selectedText === 'select' || selectedText === '--' || selectedText === 'choose' || selectedText === 'please select';
        
        isEmpty = !fieldValue || fieldValue === '' || isPlaceholderSelected || isDisabledOrEmptyOption || isSelectPlaceholder;
      } else {
        isEmpty = fieldValue === '';
      }
      
      if (isEmpty) {
        if (field.tagName === 'SELECT') {
          errors.push('Please select an option.');
        } else {
          errors.push('This field is required.');
        }
      }
    }
    
    // Only validate content if field has a value and is a text field
    if (fieldValue && (fieldType === 'text' || field.tagName === 'TEXTAREA' || fieldType === 'email' || fieldType === 'tel')) {
      // Non-Latin character check disabled on front-end
      // Submissions with non-Latin characters will still be flagged as spam on the backend (Module.php)
      // if (containsNonLatinCharacters(fieldValue)) {
      //   errors.push('This field contains invalid characters. Please use only letters, numbers, and standard punctuation.');
      // }
      
      // Check for numbers in name fields
      if (isNameField(fieldHandle, fieldLabel) && containsNumbers(fieldValue)) {
        errors.push('This field cannot contain numbers.');
      }
    }
    
    return errors;
  }

  function isNameField(fieldHandle, fieldLabel) {
    // Check if this is a name field based on handle or label
    var namePatterns = [
      'firstName', 'first_name', 'firstname', 'first',
      'lastName', 'last_name', 'lastname', 'last',
      'name', 'fullName', 'full_name',
      'givenName', 'given_name',
      'surname', 'familyName', 'family_name'
    ];
    
    var handleLower = (fieldHandle || '').toLowerCase();
    var labelLower = (fieldLabel || '').toLowerCase();
    
    // Check handle
    for (var i = 0; i < namePatterns.length; i++) {
      if (handleLower.indexOf(namePatterns[i].toLowerCase()) !== -1) {
        return true;
      }
    }
    
    // Check label
    var nameLabelPatterns = [
      /first\s*name/i,
      /last\s*name/i,
      /given\s*name/i,
      /surname/i,
      /family\s*name/i,
      /^name$/i
    ];
    
    for (var j = 0; j < nameLabelPatterns.length; j++) {
      if (nameLabelPatterns[j].test(labelLower)) {
        return true;
      }
    }
    
    return false;
  }

  function containsNumbers(text) {
    return /\d/.test(text);
  }

  function containsNonLatinCharacters(text) {
    // Check for characters outside Latin-1 range (0000-00FF)
    // This includes Basic Latin (0000-007F) and Latin-1 Supplement (0080-00FF)
    // Anything outside this range is considered non-Latin
    return /[^\x00-\xFF]/.test(text);
  }
}

